// La réponse du/de la remplaçant·e désigné·e, telle que l'écran la dit (docs/DECISIONS.md §38). Fonctions PURES, testées dans confirmation.test.ts.
// Le SERVEUR décide de l'état (`demande.confirmation_remplacant` : sous réserve, en retard, jamais enregistrée) ; ici on ne fait que le formuler.
// Une seule règle vient de l'écran : le ROUGE. Une réponse qui manque alors que la séance est dans le seuil « Urgent » du calendrier
// (réglage `calendrier.urgence_urgent_max_jours`, 7 jours) passe du violet au rouge — partout, côté intervenant·e comme côté gestion.
// Jamais la couleur seule : forme (pointillé / trait plein), icône (sablier / triangle) et mot (« Confirmation attendue », « Urgent · sans réponse »).
import type { ConfirmationRemplacant } from '../../types';
import { dePersonne } from '../messages/format';
import { joursAvant, type SeuilsUrgence } from './urgence';

/**
 * - aucune        : rien à signaler (pas de remplaçant·e, ou réponse donnée)
 * - attente       : réponse attendue, dans les temps
 * - retard        : l'échéance est atteinte sans réponse
 * - urgent        : réponse attendue ET séance dans le seuil « Urgent » (rouge)
 * - sans_reponse  : la séance est passée sans que la réponse ait jamais été enregistrée
 */
export type VueReponse = 'aucune' | 'attente' | 'retard' | 'urgent' | 'sans_reponse';

export function vueReponse(
  c: ConfirmationRemplacant | null | undefined, dateAtelier: string, seuils: SeuilsUrgence, maintenant: Date = new Date(),
): VueReponse {
  if (!c || c.confirmation == null) return 'aucune';
  if (c.sans_reponse_apres_seance) return 'sans_reponse';
  if (!c.sous_reserve) return 'aucune';
  if (joursAvant(dateAtelier.slice(0, 10), maintenant) <= seuils.urgent) return 'urgent';
  return c.en_retard ? 'retard' : 'attente';
}

/** La réponse est encore attendue (la demande avance « sous réserve »). */
export function enAttente(vue: VueReponse): boolean {
  return vue === 'attente' || vue === 'retard' || vue === 'urgent';
}

// ── Mots ─────────────────────────────────────────────────────────────────────

const JOUR_LONG = new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' });

/** « jeudi 5 novembre » (l'échéance, en heure locale). */
function jourEcheance(c: ConfirmationRemplacant): string {
  return c.echeance ? JOUR_LONG.format(new Date(c.echeance)) : '';
}

/** « avant jeudi 5 novembre » / « avant mardi 3 novembre (la veille de la séance) » ; vide si l'échéance est inconnue. */
export function avantLe(c: ConfirmationRemplacant | null | undefined): string {
  if (!c?.echeance) return '';
  return `avant ${jourEcheance(c)}${c.echeance_plafonnee ? ' (la veille de la séance)' : ''}`;
}

/** « depuis jeudi 5 novembre » : la réponse était attendue ce jour-là. */
export function depuisLe(c: ConfirmationRemplacant | null | undefined): string {
  return c?.echeance ? `depuis ${jourEcheance(c)}` : '';
}

/** « La séance a lieu demain. » quand la réponse manque et que la séance est dans 2 jours au plus ; sinon vide. */
export function seanceProche(c: ConfirmationRemplacant | null | undefined, dateAtelier: string, maintenant: Date = new Date()): string {
  if (!c?.sous_reserve) return '';
  const j = joursAvant(dateAtelier.slice(0, 10), maintenant);
  if (j < 0 || j > 2) return '';
  return `La séance a lieu ${j === 0 ? 'aujourd’hui' : j === 1 ? 'demain' : `dans ${j} jours`}.`;
}

/** Un texte avec des mots en gras, sans HTML : le composant l'affiche (`<b>`) ; le test le compare tel quel. */
export type Segment = string | { b: string };
export function enTexte(segments: Segment[]): string {
  return segments.map((s) => (typeof s === 'string' ? s : s.b)).join('');
}

/** Le mot du badge. `pourLaPersonneDesignee` : c'est elle qui regarde (« À vous de confirmer »). */
export function libelleBadgeReponse(vue: VueReponse, pourLaPersonneDesignee = false): string {
  // La séance a eu lieu sans réponse : plus rien à « confirmer » (le serveur refuse), même pour la personne désignée.
  if (vue === 'sans_reponse') return 'Confirmation non enregistrée';
  if (pourLaPersonneDesignee) return vue === 'urgent' ? 'Urgent · à vous de confirmer' : 'À vous de confirmer';
  switch (vue) {
    case 'urgent': return 'Urgent · sans réponse';
    case 'retard': return 'Réponse en retard';
    case 'attente': return 'Confirmation attendue';
    default: return '';
  }
}

/** Classes du badge : tireté tant que c'est dans les temps ; trait plein en retard, en urgence ou si la réponse n'a jamais été enregistrée. */
export function classeBadgeReponse(vue: VueReponse): string {
  switch (vue) {
    case 'urgent': return 'badge badge-reserve urgent';
    case 'retard':
    case 'sans_reponse': return 'badge badge-reserve retard';
    default: return 'badge badge-reserve';
  }
}

/** Triangle d'alerte (retard, urgence, réponse jamais enregistrée) plutôt que sablier (dans les temps). */
export function iconeAlerteBadge(vue: VueReponse): boolean {
  return vue === 'retard' || vue === 'urgent' || vue === 'sans_reponse';
}

// ── La frise de la fiche : l'étape 2 reste « sous réserve » ──────────────────

export interface EtapeReserve {
  /** Sous le rond de l'étape (petit, visible). */
  sous: string;
  /** Pour le lecteur d'écran (le rond ne montre qu'une icône). */
  lecteur: string;
  alerte: boolean;
  urgent: boolean;
}

/** « de » ou « d' » seul devant un nom (élision devant une voyelle ou un h), pour les phrases où le nom est en gras : « de Camille », « d'Alexia ». */
const deDevant = (nom: string): string => dePersonne(nom).slice(0, dePersonne(nom).length - nom.trim().length);

/** Ce que dit l'étape « Confirmation » de la frise tant que la réponse manque ; null si tout est en ordre. */
export function etapeReserve(vue: VueReponse, c: ConfirmationRemplacant | null | undefined, nom: string, pourLaPersonneDesignee: boolean): EtapeReserve | null {
  if (vue === 'aucune') return null;
  const de = pourLaPersonneDesignee ? 'de votre part' : dePersonne(nom);
  const retard = vue === 'retard' || (vue === 'urgent' && c?.en_retard === true);
  if (vue === 'sans_reponse') return { sous: 'non enregistrée', lecteur: `réponse jamais enregistrée ${de}`, alerte: true, urgent: false };
  return {
    sous: retard ? 'en retard' : vue === 'urgent' ? 'urgent' : 'en attente',
    lecteur: retard ? `réponse en retard ${de}` : vue === 'urgent' ? `réponse en attente ${de}, séance proche` : `réponse en attente ${de}`,
    alerte: retard || vue === 'urgent',
    urgent: vue === 'urgent',
  };
}

/** La note sous la frise : où en est la réponse, en une phrase. Null si rien à dire (ou si la personne désignée n'a rien à lire après la séance). */
export function noteFrise(
  vue: VueReponse, c: ConfirmationRemplacant | null | undefined, dateAtelier: string, nom: string, pourLaPersonneDesignee: boolean, maintenant: Date = new Date(),
): { alerte: boolean; urgent: boolean; segments: Segment[] } | null {
  if (vue === 'aucune' || !c) return null;
  if (vue === 'sans_reponse') {
    return pourLaPersonneDesignee ? null
      : { alerte: true, urgent: false, segments: [{ b: 'Confirmation non enregistrée.' }, ` La séance a eu lieu sans réponse ${dePersonne(nom)} dans l’application.`] };
  }
  const avant = avantLe(c);
  const fin = seanceProche(c, dateAtelier, maintenant);
  const suite: Segment[] = fin ? [` ${fin}`] : [];
  const urgent = vue === 'urgent';
  if (pourLaPersonneDesignee) {
    return c.en_retard
      ? { alerte: true, urgent, segments: [{ b: `Votre réponse était attendue ${avant}.` }, ...suite] }
      : { alerte: false, urgent, segments: [{ b: `Votre réponse est attendue ${avant}.` }, ...suite] };
  }
  return c.en_retard
    ? { alerte: true, urgent, segments: [{ b: `${nom} n’a pas répondu` }, ` (réponse attendue ${avant}).`, ...suite] }
    : { alerte: false, urgent, segments: [`Réponse ${deDevant(nom)}`, { b: nom }, ` attendue ${avant}.`, ...suite] };
}

// ── La bande « sous réserve » de la carte « Prochaine étape » ────────────────

export interface BandeReponse {
  alerte: boolean;
  urgent: boolean;
  segments: Segment[];
}

export function bandeReponse(
  vue: VueReponse, c: ConfirmationRemplacant | null | undefined, dateAtelier: string, nom: string, maintenant: Date = new Date(),
): BandeReponse | null {
  if (!enAttente(vue) || !c) return null;
  const fin = seanceProche(c, dateAtelier, maintenant);
  const titre = vue === 'urgent' ? 'Urgent : sans réponse.' : c.en_retard ? 'Réponse en retard.' : 'Confirmation attendue.';
  const corps = c.en_retard
    ? ` ${nom} n’a pas répondu (réponse attendue ${depuisLe(c)}).`
    : ` ${nom} n’a pas encore confirmé (réponse attendue ${avantLe(c)}).`;
  return { alerte: c.en_retard, urgent: vue === 'urgent', segments: [{ b: titre }, corps, ...(fin ? [` ${fin}`] : [])] };
}

/** « Vos remplacements » : la ligne d'échéance de la réponse attendue. */
export function ligneEcheance(vue: VueReponse, c: ConfirmationRemplacant | null | undefined, dateAtelier: string, maintenant: Date = new Date()): BandeReponse | null {
  if (!enAttente(vue) || !c) return null;
  const fin = seanceProche(c, dateAtelier, maintenant);
  const segments: Segment[] = c.en_retard
    ? [{ b: 'On attend toujours votre réponse' }, ` (attendue ${avantLe(c)}).`]
    : [{ b: `Répondez ${avantLe(c)}.` }];
  return { alerte: c.en_retard, urgent: vue === 'urgent', segments: fin ? [...segments, ` ${fin}`] : segments };
}
